import React, { useState, useEffect } from 'react';
import Stats from './Stats';
import LicenseTable from './LicenseTable';
import AddLicenseDialog from './AddLicenseDialog';

interface DashboardProps {
  onLogout: () => void;
  apiUrl: string;
  authToken: string;
}

interface StatsData {
  totalLicenses: number;
  totalUniqueIds: number;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout, apiUrl, authToken }) => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/stats`, {
        headers: {
          'Authorization': `Basic ${authToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        // Unauthorized - token might be invalid, log out
        console.error('Authentication failed, logging out');
        onLogout();
      } else {
        console.error('Failed to fetch stats:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // If it's a network error, the API URL might be wrong
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('Network error - check if API URL is correct:', apiUrl);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, apiUrl, authToken]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold">License Server Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAddDialog(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add License
              </button>
              <button
                onClick={onLogout}
                className="text-gray-700 hover:text-gray-900 px-4 py-2 rounded-md"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <>
            <Stats stats={stats} />
            <LicenseTable
              apiUrl={apiUrl}
              authToken={authToken}
              onRefresh={handleRefresh}
              refreshKey={refreshKey}
            />
          </>
        )}
      </main>

      {showAddDialog && (
        <AddLicenseDialog
          apiUrl={apiUrl}
          authToken={authToken}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;

